/**
 * MaintenanceScreen Component - Phase 68 M5 Task 5.3
 *
 * Full-page maintenance mode screen shown to non-super-admin users
 * when maintenance mode is active.
 *
 * Design reference: tasks.md § 5.3 MaintenanceScreen Component
 */
import React from 'react'
import { Box, Typography, useTheme } from '@mui/material'
import BuildIcon from '@mui/icons-material/Build'

interface MaintenanceScreenProps {
  /** Custom message from maintenance mode configuration */
  message?: string | null
}

/**
 * Full-page maintenance screen.
 *
 * Displays:
 * - NEXO branding at top
 * - Maintenance icon
 * - Default or custom message
 * - Works in both light and dark themes
 */
const MaintenanceScreen: React.FC<MaintenanceScreenProps> = ({ message }) => {
  const theme = useTheme()

  const defaultMessage = 'La plataforma se encuentra en mantenimiento. Volveremos pronto.'
  const displayMessage = message || defaultMessage

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: theme.palette.background.default,
        color: theme.palette.text.primary,
        px: 3,
        textAlign: 'center',
      }}
    >
      {/* NEXO Logo/Branding */}
      <Typography
        variant="h3"
        sx={{
          fontFamily: '"Bebas Neue", sans-serif',
          fontWeight: 700,
          letterSpacing: '0.1em',
          color: theme.palette.primary.main,
          mb: 4,
        }}
      >
        NEXO
      </Typography>

      {/* Maintenance Icon */}
      <Box
        sx={{
          width: 80,
          height: 80,
          borderRadius: '50%',
          bgcolor: theme.palette.warning.light,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mb: 3,
        }}
      >
        <BuildIcon
          sx={{
            fontSize: 40,
            color: theme.palette.warning.dark,
          }}
        />
      </Box>

      {/* Title */}
      <Typography
        variant="h5"
        sx={{
          fontWeight: 600,
          mb: 2,
          color: theme.palette.text.primary,
        }}
      >
        Mantenimiento en Progreso
      </Typography>

      {/* Message */}
      <Typography
        variant="body1"
        sx={{
          maxWidth: 400,
          color: theme.palette.text.secondary,
          lineHeight: 1.6,
        }}
      >
        {displayMessage}
      </Typography>
    </Box>
  )
}

export default MaintenanceScreen
